import { Dispatch } from '@reduxjs/toolkit';
import { keyBy, uniqBy } from 'lodash/fp';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType } from 'dashboard/models/account-type';
import { AccountTypeGridLayout } from 'dashboard/models/account-type-grid-layouts';
import { AccountVariableDefinition } from 'dashboard/models/account-variable-definition';
import { Case } from 'dashboard/models/case';
import { CaseTemplate, CaseTemplateStatus } from 'dashboard/models/case-template';
import { Device } from 'dashboard/models/device';
import { Document } from 'dashboard/models/document';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Report, SUPPORTED_REPORT_TYPES } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Selfie } from 'dashboard/models/selfie';
import { Tag } from 'dashboard/models/tag';
import { User } from 'dashboard/models/user';
import { Verification } from 'dashboard/models/verification';
import {
  saveAccountDocuments,
  saveAccountInquiries,
  saveAccountReports,
  saveAccountVerifications,
  setAccount,
  setAccountCases,
  setAccountCaseTemplates,
  setAccountDevices,
  setAccountDocuments,
  setAccountListItems,
  setAccountListMatches,
  setAccountLists,
  setAccountRedactor,
  setAccountRelations,
  setAccountReports,
  setAccountReportTemplates,
  setAccountSelfies,
  setAccountStatus,
  setAccountType,
  setAccountVariableDefinitions,
  setTagsSummary,
} from 'dashboard/redux/slices/account';
import { setAccountTypeGridLayouts } from 'dashboard/redux/slices/account-type';
import { ApiEndpoint, ApiListResponse, ApiModel, PaginationParams } from 'lib/interfaces/api';
import { pickRelationsFromAPIResponse } from 'lib/relations';

export function loadAccount(id: string, skipDocuments?: boolean) {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.getResource<Account>(ApiEndpoint.Account, id);
    const { data: account, included = [] } = response;

    const includedItemsMap = keyBy('id', included);

    const redactor =
      account.relationships.redactor?.data != null
        ? (includedItemsMap[account.relationships.redactor.data.id] as User)
        : undefined;
    dispatch(setAccountRedactor(redactor));

    const allTags = included.filter(
      (apiRelationship) => apiRelationship.type === ApiModel.Tag,
    ) as Tag[];

    const tags = uniqBy('id', allTags);
    dispatch(setTagsSummary(tags));

    const accountStatus =
      account.relationships['account-status']?.data != null
        ? (includedItemsMap[account.relationships['account-status'].data.id] as AccountStatus)
        : undefined;
    dispatch(setAccountStatus(accountStatus));

    const accountType =
      account.relationships['account-type']?.data != null
        ? (includedItemsMap[account.relationships['account-type'].data.id] as AccountType)
        : undefined;
    dispatch(setAccountType(accountType));

    const allReports = included.filter((apiRelationship) =>
      SUPPORTED_REPORT_TYPES.includes(apiRelationship.type),
    ) as Report[];
    const reports = uniqBy('id', allReports);
    dispatch(setAccountReports(reports));

    const devices = included.filter(
      (apiRelationship) => apiRelationship.type === ApiModel.Device,
    ) as Device[];
    dispatch(setAccountDevices(devices));

    const selfies = included.filter(
      (apiRelationship) =>
        apiRelationship.type === ApiModel.SelfieCenterOnly ||
        apiRelationship.type === ApiModel.SelfieProfileAndCenter,
    ) as Selfie[];
    dispatch(setAccountSelfies(selfies));

    const allDocuments = included.filter(
      (apiRelationship) => apiRelationship.type === ApiModel.DocumentGeneric,
    ) as Document[];

    if (!skipDocuments) {
      const documents = uniqBy('id', allDocuments);
      dispatch(setAccountDocuments(documents));
    }

    const allCases = included.filter(
      (apiRelationship) => apiRelationship.type === ApiModel.Case,
    ) as Case[];

    const cases = uniqBy('id', allCases);
    dispatch(setAccountCases(cases));

    const accountTypeGridLayouts = included.filter((object) => {
      return object.type === ApiModel.AccountTypeGridLayout;
    }) as AccountTypeGridLayout[];
    dispatch(setAccountTypeGridLayouts(accountTypeGridLayouts));

    const accountVariableDefinitions = included.filter((object) => {
      return object.type === ApiModel.AccountVariableDefinition;
    }) as AccountVariableDefinition[];
    dispatch(setAccountVariableDefinitions(accountVariableDefinitions));

    const allListMatches = included.filter(
      (apiRelationship) => apiRelationship.type === ApiModel.ListMatch,
    ) as ListMatch[];
    dispatch(setAccountListMatches(allListMatches));

    const lists = allListMatches.map(
      (listMatch) => includedItemsMap[listMatch.relationships.list.data.id],
    ) as List[];
    dispatch(setAccountLists(lists));

    const listItems = allListMatches.map(
      (listMatch) => includedItemsMap[listMatch.relationships['list-item'].data.id],
    ) as ListItem[];
    dispatch(setAccountListItems(listItems));

    dispatch(setAccountRelations(pickRelationsFromAPIResponse(response)));

    dispatch(setAccount(account));
  };
}

export function loadAccountInquiries({
  accountId,
  currentEnvironment,
  currentPage,
  pageSize,
}: {
  accountId: string;
  currentEnvironment: Environment;
  currentPage: number;
  pageSize: number;
}) {
  return async (dispatch: Dispatch) => {
    const params = {
      filter: {
        'environment-id': currentEnvironment.id,
        account_ids: [accountId],
      },
      page: {
        number: currentPage,
        size: pageSize,
      },
      sort: '-created_at',
    };

    const inquiriesResponse = await SessionClientDashboard.listResource<Inquiry, PaginationParams>(
      ApiEndpoint.Inquiry,
      params,
    );

    const inquiries: Inquiry[] = inquiriesResponse.data;
    const paginationParams = inquiriesResponse.meta;
    dispatch(saveAccountInquiries({ inquiries, paginationParams }));
  };
}

export function loadAccountReportTemplates(accountId: string) {
  return async (dispatch: Dispatch) => {
    const params = {
      filter: {
        'account-id': accountId,
      },
    };

    const reportTemplatesResponse = await SessionClientDashboard.listResource<
      ReportTemplate,
      PaginationParams
    >(ApiEndpoint.ReportTemplateCreateable, params);
    const reportTemplates = reportTemplatesResponse.data;
    dispatch(setAccountReportTemplates(reportTemplates));
  };
}

export function loadAccountCaseTemplates() {
  return async (dispatch: Dispatch) => {
    const params = {
      filter: {
        status: CaseTemplateStatus.Active,
      },
      page: {
        // use a large page size in case an organization has many case templates
        size: 100,
      },
    };

    const caseTemplatesResponse = await SessionClientDashboard.listResource<
      CaseTemplate,
      PaginationParams
    >(ApiEndpoint.CaseTemplate, params);
    const caseTemplates = caseTemplatesResponse.data;
    dispatch(setAccountCaseTemplates(caseTemplates));
  };
}

export function loadAccountVerifications({
  accountId,
  currentPage,
  pageSize,
}: {
  accountId: string;
  currentPage: number;
  pageSize: number;
}) {
  return async (dispatch: Dispatch) => {
    const params = {
      page: {
        number: currentPage,
        size: pageSize,
      },
      sort: '-created_at',
    };

    const verificationsResponse = await SessionClientDashboard.get<
      ApiListResponse<Verification, PaginationParams>
    >(ApiEndpoint.Account, accountId, 'verification-objects', params);

    const verifications = verificationsResponse.data;
    const paginationParams = verificationsResponse.meta;

    dispatch(saveAccountVerifications({ verifications, paginationParams }));
  };
}

export function loadAccountReports({
  accountId,
  currentPage,
  pageSize,
}: {
  accountId: string;
  currentPage: number;
  pageSize: number;
}) {
  return async (dispatch: Dispatch) => {
    const params = {
      page: {
        number: currentPage,
        size: pageSize,
      },
      sort: '-created_at',
    };
    const reportsResponse = await SessionClientDashboard.get<
      ApiListResponse<Report, PaginationParams>
    >(ApiEndpoint.Account, accountId, 'report-objects', params);
    const reports = reportsResponse.data;
    const paginationParams = reportsResponse.meta;
    dispatch(saveAccountReports({ reports, paginationParams }));
  };
}

export function loadAccountDocuments({
  accountId,
  currentPage,
  pageSize,
}: {
  accountId: string;
  currentPage: number;
  pageSize: number;
}) {
  return async (dispatch: Dispatch) => {
    const params = {
      page: {
        number: currentPage,
        size: pageSize,
      },
      sort: '-created_at',
    };
    const documentsResponse = await SessionClientDashboard.get<
      ApiListResponse<Document, PaginationParams>
    >(ApiEndpoint.Account, accountId, 'document-objects', params);
    const documents = documentsResponse.data;
    const paginationParams = documentsResponse.meta;
    dispatch(saveAccountDocuments({ documents, paginationParams }));
  };
}
